import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useGetBillingInfo,
    useGrantCredits,
    useOrganization,
    useReloadBillingInfo,
} from "@/queries/organization-queries";
import { OCUs } from "@/routes/settings/organization/billing/OCU";
import { formatError } from "@/utils/errors";
import { CreditStatus } from "gitpod-next-api/gitpod/v1/billing_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { InfoIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { creditsToDollars, formatCredits } from "./billing-utils";

export const CreditValueText: FC<{ credits: number; showDollarAmount?: boolean }> = ({
    credits,
    showDollarAmount = false,
}) => {
    const dollars = creditsToDollars(credits);
    return (
        <Text className="text-2xl font-semibold text-content-primary">
            {formatCredits(credits)}
            {showDollarAmount && (
                <span className="pl-2 text-md text-content-secondary">
                    {dollars > 0 ? `($${dollars.toFixed(2)})` : ""}
                </span>
            )}
        </Text>
    );
};

export const CreditBalance: FC = () => {
    const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

    const { data: billingInfo, isPending: isLoadingBillingInfo } = useGetBillingInfo();
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { reload: reloadBillingInfo } = useReloadBillingInfo();

    const tier = organization?.tier;
    const isOnCoreTier = tier === OrganizationTier.CORE;

    // Poll for credits update
    // This is a workaround to avoid the issue where the credits are not updated immediately after a purchase
    // * max 10 seconds
    // * if the credits are updated, the polling will stop
    const [isPolling, setIsPolling] = useState(false);
    const _currentTotalCredits = useRef(billingInfo?.totalCredits);
    useEffect(() => {
        _currentTotalCredits.current = billingInfo?.totalCredits ?? 0;
    }, [billingInfo]);
    const pollForCreditsUpdate = useCallback(() => {
        const initialTotalCredits = billingInfo?.totalCredits ?? 0;
        let countdown = 10;
        setIsPolling(true);
        const interval = setInterval(() => {
            countdown--;
            if (countdown <= 0 || _currentTotalCredits.current !== initialTotalCredits) {
                clearInterval(interval);
                setIsPolling(false);
            } else {
                reloadBillingInfo(false);
            }
        }, 1000);

        return () => {
            clearInterval(interval);
            setIsPolling(false);
        };
    }, [reloadBillingInfo, billingInfo]);

    // If billing is not set up, don't show the card
    if (isLoadingBillingInfo || isPendingOrganization || !billingInfo) {
        return null;
    }

    const usagePercentage =
        billingInfo.totalCredits > 0 ? Math.round((billingInfo.availableCredits / billingInfo.totalCredits) * 100) : 0;
    const isOutOfCredits = billingInfo.creditStatus === CreditStatus.OUT_OF_CREDITS;
    const isLowOnCredits = billingInfo.creditStatus === CreditStatus.LOW_ON_CREDITS;

    return (
        <Card className="bg-surface-glass p-6" variant="bordered">
            <div className="mb-6">
                <h2 className="flex flex-row items-center text-lg font-semibold text-content-primary">
                    Ona Compute Units
                    {isPolling && <IconSpinner size={"sm"} className="ml-2 animate-spin" />}
                </h2>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-8">
                <div>
                    <Text className="mb-1 text-sm text-content-secondary">Total OCUs</Text>
                    <CreditValueText credits={billingInfo.totalCredits} showDollarAmount />
                </div>
                <div>
                    <Text className="mb-1 text-sm text-content-secondary">Used OCUs</Text>
                    <CreditValueText credits={billingInfo.usedCredits} />
                </div>
                <div>
                    <Text className="mb-1 text-sm text-content-secondary">Available OCUs</Text>
                    <CreditValueText credits={billingInfo.availableCredits} />
                </div>
            </div>

            <div className="mb-6">
                <div className="h-4 w-full rounded-full border border-surface-04 bg-surface-glass p-0.5">
                    <div className="h-full overflow-hidden rounded-full bg-surface-glass">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-orange-100 to-orange-300 transition-all duration-300"
                            style={{ width: `${usagePercentage}%` }}
                        />
                    </div>
                </div>
            </div>

            {isOnCoreTier && (
                <div className="flex items-center justify-between">
                    <div>
                        <Text className="font-semibold text-content-primary">
                            Add more <OCUs />
                        </Text>
                        <Text className="text-sm text-content-secondary">
                            Give your team continued access after they hit their plan limit.
                        </Text>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => setShowBuyCreditsModal(true)}
                        data-tracking-id="top-up-credit-balance"
                    >
                        Top-up
                    </Button>
                </div>
            )}

            {(isOutOfCredits || isLowOnCredits) && (
                <div className="mt-4 flex items-center justify-between rounded-lg bg-surface-yield p-4">
                    <Text className="flex items-center gap-2 text-sm text-content-secondary">
                        <InfoIcon className="size-4" />
                        {isOutOfCredits && <span>You&apos;ve run out of OCUs. </span>}
                        {isLowOnCredits && <span>You&apos;re running low on OCUs. </span>}
                        {isOnCoreTier && <span>Top up now to stay on track.</span>}
                    </Text>
                </div>
            )}

            <TopUpModal
                open={showBuyCreditsModal}
                onClose={(success) => {
                    setShowBuyCreditsModal(false);
                    if (success) {
                        pollForCreditsUpdate();
                    }
                }}
            />
        </Card>
    );
};

const createCreditsItem = (credits: number) => {
    const dollars = creditsToDollars(credits);
    return {
        value: String(credits),
        label: `${credits} OCUs – $${dollars}`,
    };
};
const creditAmounts = [
    createCreditsItem(40),
    createCreditsItem(100),
    createCreditsItem(200),
    createCreditsItem(400),
    createCreditsItem(1000),
];

type TopUpModalProps = {
    open: boolean;
    onClose: (success?: boolean) => void;
};

const TopUpModal: FC<TopUpModalProps> = ({ open, onClose }) => {
    const [selectedAmount, setSelectedAmount] = useState<string>("100");
    const { toast } = useToast();
    const grantCreditsMutation = useGrantCredits();

    const handlePurchase = async () => {
        try {
            // Grant credits using the mutation
            await grantCreditsMutation.mutateAsync(Number(selectedAmount));

            toast({
                title: "OCUs added!",
                description: `${selectedAmount} OCUs are now available for use.`,
            });

            onClose(true);
        } catch (error) {
            toast({
                title: "Unable to add OCUs",
                description: "Something went wrong: " + formatError(error),
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.BillingModal}>
                <DialogHeader>
                    <DialogTitle>Top-up your Ona Compute Units</DialogTitle>
                    <DialogDescription>
                        Add <OCUs /> to continue using Ona. This one-time purchase is available immediately and
                        won&apos;t change your subscription.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="amount-select" className="text-md font-medium text-content-primary">
                            Select amount
                        </label>
                        <Select value={selectedAmount} onValueChange={setSelectedAmount}>
                            <SelectTrigger id="amount-select" loading={false}>
                                <SelectValue placeholder="Select amount" />
                            </SelectTrigger>
                            <SelectContent>
                                {creditAmounts.map((amount) => (
                                    <SelectItem key={amount.value} value={amount.value} className="px-4 py-2 text-base">
                                        <div>{amount.label}</div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary" disabled={grantCreditsMutation.isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        onClick={handlePurchase}
                        loading={grantCreditsMutation.isPending}
                        disabled={grantCreditsMutation.isPending}
                        data-tracking-id="purchase"
                    >
                        Purchase
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
